import axios from 'axios';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';

/**
 * Mismo flujo que Buscador: Swal → PUT existencias → GET movimientos-recientes.
 * @returns {Promise<null | {
 *   stock_actual: number,
 *   tieneMovimientosRecientes: boolean,
 *   nombre_empleado: string|null,
 *   fecha_ultimo_movimiento: string|null,
 * }>}
 */
export async function grabarConteoProducto({
  token,
  idProducto,
  nombreProducto,
  stockActual = 0,
  idEmpleado: idEmpleadoProp,
}) {
  if (!idProducto) {
    await Swal.fire('Conteo', 'Producto sin ID; no se puede contar.', 'warning');
    return null;
  }
  if (!token) {
    await Swal.fire('Sesión', 'Debes iniciar sesión para grabar el conteo.', 'warning');
    return null;
  }

  const idEmpleado = idEmpleadoProp || Cookies.get('id_empleado');
  const { value: nuevaCantidad } = await Swal.fire({
    title: 'Contar existencias',
    html: `<b class="text-lg">${nombreProducto || `Producto #${idProducto}`}</b>
      <p class="text-sm text-slate-600 mt-2">Indica cuántas unidades hay realmente en bodega.</p>`,
    input: 'number',
    inputLabel: 'Cantidad contada',
    inputValue: Math.max(0, Math.round(Number(stockActual) || 0)),
    inputAttributes: { min: 0, step: 1 },
    showCancelButton: true,
    confirmButtonText: 'Grabar conteo',
    cancelButtonText: 'Cancelar',
    confirmButtonColor: '#059669',
  });

  if (nuevaCantidad === undefined || Number.isNaN(Number(nuevaCantidad))) {
    return null;
  }

  const cantidad = Math.max(0, Number(nuevaCantidad));
  const headers = { Authorization: `Bearer ${token}` };

  try {
    await axios.put(
      '/api/productos/existencias',
      { idProducto, cantidad, idEmpleado },
      { headers },
    );
    const { data: movimientoReciente } = await axios.get(
      `/api/productos/movimientos-recientes/${idProducto}`,
      { headers },
    );
    await Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Conteo grabado',
      timer: 2000,
      showConfirmButton: false,
    });
    return {
      stock_actual: cantidad,
      tieneMovimientosRecientes: Boolean(movimientoReciente?.tieneMovimientosRecientes),
      nombre_empleado: movimientoReciente?.nombre_empleado || null,
      fecha_ultimo_movimiento: movimientoReciente?.fecha_ultimo_movimiento || null,
    };
  } catch (err) {
    const status = err.response?.status;
    const msg = err.response?.data?.message
      || err.response?.data?.error
      || err.message
      || 'Error al grabar el conteo';
    if (status === 403) {
      await Swal.fire(
        'Sin permiso',
        'No tienes permiso para grabar conteos (productos.conteo.grabar).',
        'error',
      );
    } else {
      await Swal.fire('Error', msg, 'error');
    }
    return null;
  }
}

/**
 * Si falta conteo reciente, obliga a contar antes de continuar.
 * @returns {Promise<false | object>} false si cancela; objeto conteo si OK / ya contado
 */
export async function ensureConteoAntesDeAnadir({
  token,
  idProducto,
  nombreProducto,
  stockActual = 0,
  tieneMovimientosRecientes,
  idEmpleado,
}) {
  if (tieneMovimientosRecientes) {
    return {
      stock_actual: stockActual,
      tieneMovimientosRecientes: true,
    };
  }

  const ask = await Swal.fire({
    title: 'Hay que contar primero',
    html: `Este producto <b>no tiene conteo reciente</b>. El stock del sistema puede estar desactualizado.<br/><br/>Cuéntalo antes de añadirlo a la orden.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Contar ahora',
    cancelButtonText: 'Cancelar',
    confirmButtonColor: '#d97706',
  });
  if (!ask.isConfirmed) return false;

  const result = await grabarConteoProducto({
    token,
    idProducto,
    nombreProducto,
    stockActual,
    idEmpleado,
  });
  if (!result?.tieneMovimientosRecientes) return false;
  return result;
}
